import React, { useState, useContext, useEffect, useMemo } from "react";
import { Tree, Button } from "antd";
import { useQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";

import { GET_LEAD_SOURCES_DS } from "../../../../graphql/query";
import {
  setOpportunitySourceFilters,
  setOpportunitySourceFilterCount,
} from "../../../../redux/slices/leadSlice";
import { UserContext } from "../../../../amplify/authenticator-provider";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";
import { toggleTreeKey } from "../../../../helpers/checkableTree";

const computeParentCount = (checkedKeys, treeData) => {
  const parentMap = {};
  const buildMap = (nodes, parentKey = null) => {
    nodes.forEach(node => {
      parentMap[String(node.key)] = parentKey;
      if (node.children?.length > 0) buildMap(node.children, String(node.key));
    });
  };
  buildMap(treeData);
  const checkedSet = new Set(checkedKeys.map(String));
  return checkedKeys.filter(key => {
    const parent = parentMap[String(key)];
    return !parent || !checkedSet.has(parent);
  }).length;
};

const getLocationIds = () => {
  const locationId = sessionStorage.getItem("currentLocationId");
  const ids = String(locationId || "").includes(",") ? locationId.split(",") : [locationId];
  return ids.map(id => +id);
};

// Convert API tree structure (parents/childs) → Ant Design treeData format
const toAntTreeData = sources => {
  if (!sources?.length) return [];
  return sources
    .filter(s => s.name?.trim())
    .map(source => {
      const node = { title: source.name, key: String(source.key) };
      const children = source.parents || source.childs;
      if (children?.length > 0) {
        node.children = toAntTreeData(children);
      }
      return node;
    })
    .sort((a, b) => (a.title || "").toLowerCase().localeCompare((b.title || "").toLowerCase()));
};

const OpportunitySourceContent = ({ search = "", scrollListToTop, onChange, onClear }) => {
  const dispatch = useDispatch();
  const { user } = useContext(UserContext);
  const opportunitySourceFilters =
    useSelector(state => state.lead.filters.opportunitySourceFilters) || [];
  const opportunitySourceFilterCount = useSelector(
    state => state.lead.filters.opportunitySourceFilterCount || 0
  );
  const [expandedNodes, setExpandedNodes] = useState([]);

  const { data, loading } = useQuery(GET_LEAD_SOURCES_DS, {
    variables: {
      companyId: parseInt(user?.company?.id),
      locationIds: getLocationIds(),
    },
    fetchPolicy: "cache-first",
  });

  const treeData = useMemo(() => toAntTreeData(data?.getLeadSources), [data]);

  // Reconcile the stored chip count with the tree (EAI-1889) — the count is
  // dispatched separately from the ids, so restored state can arrive with ids
  // and a zero/provisional count, and `computeParentCount` needs this tree.
  useEffect(() => {
    if (loading || treeData.length === 0) return;
    const exact = computeParentCount(opportunitySourceFilters, treeData);
    if (exact !== opportunitySourceFilterCount) {
      dispatch(setOpportunitySourceFilterCount(exact));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, treeData, opportunitySourceFilters, opportunitySourceFilterCount]);

  const filteredTreeData = useMemo(() => {
    if (!search) return treeData;
    const lowerSearch = search.toLowerCase();
    const filterNodes = nodes =>
      nodes
        .map(node => {
          const filteredChildren = node.children ? filterNodes(node.children) : [];
          const selfMatch = node.title?.toLowerCase().includes(lowerSearch);
          if (selfMatch || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren.length > 0 ? filteredChildren : node.children,
            };
          }
          return null;
        })
        .filter(Boolean);
    return filterNodes(treeData);
  }, [treeData, search]);

  // Auto-expand all filtered parents only when searching
  // (default: collapsed — only parent nodes visible; user controls expansion manually)
  useEffect(() => {
    if (!search) return;
    const keys = [];
    const collect = nodes =>
      (nodes || []).forEach(n => {
        if (n.children?.length > 0) {
          keys.push(n.key);
          collect(n.children);
        }
      });
    collect(filteredTreeData);
    setExpandedNodes(keys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const allParentKeys = useMemo(() => {
    const keys = [];
    const collect = nodes =>
      (nodes || []).forEach(n => {
        if (n.children?.length > 0) {
          keys.push(n.key);
          collect(n.children);
        }
      });
    collect(filteredTreeData);
    return keys;
  }, [filteredTreeData]);

  const isAllExpanded = expandedNodes.length >= allParentKeys.length;

  // Shared by the checkbox and the row title, so clicking the name toggles it.
  const handleToggle = key => {
    const keys = toggleTreeKey(treeData, opportunitySourceFilters, key).map(Number);
    dispatch(setOpportunitySourceFilters(keys));
    dispatch(setOpportunitySourceFilterCount(computeParentCount(keys, treeData)));
    scrollListToTop?.();
    onChange?.();
  };

  if (loading) {
    return (
      <div className="px-3 py-2">
        <CustomSkeleton
          skeletonCount={5}
          skeletonMarginBottom="7px"
          skeletonWidth="28rem"
          skeletonHeight="2rem"
        />
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      <div className="flex items-center justify-between mb-2">
        {allParentKeys.length > 0 ? (
          <Button
            type="link"
            size="small"
            className="!p-0 !h-auto"
            onClick={() => setExpandedNodes(isAllExpanded ? [] : allParentKeys)}
          >
            {isAllExpanded ? "Collapse all" : "Expand all"}
          </Button>
        ) : (
          <span />
        )}
        {onClear && opportunitySourceFilters.length > 0 && (
          <Button type="link" size="small" className="!p-0 !h-auto" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
      <Tree
        checkable
        checkedKeys={opportunitySourceFilters.map(String)}
        expandedKeys={expandedNodes}
        onExpand={keys => setExpandedNodes([...keys])}
        selectedKeys={[]}
        onCheck={(_, { node }) => handleToggle(node.key)}
        onSelect={(_, { node }) => handleToggle(node.key)}
        treeData={filteredTreeData}
      />
    </div>
  );
};

export default OpportunitySourceContent;
